import type {
  ExerciseNode,
  Expr,
  Line,
  Mods,
  Prop,
  SetLine,
  Stmt,
  TemplateNode,
  WorkoutNode,
} from './ast'
import { parseTemplate } from './parser'
import { formatNumber } from './values'

/**
 * The tree back into text, one way only. An editor writes what comes out of here, so a script
 * edited through the GUI and the same script typed by hand are the same file — which is also
 * what lets a block be recognised by its shape.
 */

const INDENT = '  '

export function serialize(template: TemplateNode): string {
  const out: string[] = []
  out.push(`mixedscript ${template.version}\n\n`)
  template.comments.forEach((comment) => out.push(`// ${comment}\n`))
  out.push(`template ${quote(template.name)}${props(template.props)} {\n`)
  template.workouts.forEach((workout, index) => {
    if (index > 0) {
      out.push('\n')
    }
    serializeWorkout(out, workout, 1)
  })
  out.push('}\n')
  return out.join('')
}

function serializeWorkout(out: string[], workout: WorkoutNode, depth: number): void {
  const pad = INDENT.repeat(depth)
  workout.comments.forEach((comment) => out.push(`${pad}// ${comment}\n`))
  out.push(`${pad}workout ${quote(workout.name)}${props(workout.props)} {\n`)
  workout.items.forEach((item, index) => {
    if (index > 0) {
      out.push('\n')
    }
    if (item.type === 'exercise') {
      serializeExercise(out, item.exercise, depth + 1)
    } else {
      item.comments.forEach((comment) => out.push(`${pad}${INDENT}// ${comment}\n`))
      out.push(`${pad}${INDENT}${item.kind}${props(item.props)} {\n`)
      item.exercises.forEach((exercise, position) => {
        if (position > 0) {
          out.push('\n')
        }
        serializeExercise(out, exercise, depth + 2)
      })
      out.push(`${pad}${INDENT}}\n`)
    }
  })
  out.push(`${pad}}\n`)
}

function serializeExercise(out: string[], exercise: ExerciseNode, depth: number): void {
  const pad = INDENT.repeat(depth)
  exercise.comments.forEach((comment) => out.push(`${pad}// ${comment}\n`))
  out.push(`${pad}exercise ${quote(exercise.name)}${props(exercise.props)} {\n`)
  for (const line of exercise.lines) {
    line.comments.forEach((comment) => out.push(`${pad}${INDENT}// ${comment}\n`))
    serializeLine(out, line, depth + 1)
  }
  out.push(`${pad}}\n`)
}

export function serializeLine(out: string[], line: Line, depth: number): void {
  const pad = INDENT.repeat(depth)
  if (line.type === 'set') {
    out.push(pad + setLine(line) + '\n')
    return
  }
  if (line.type === 'auto') {
    const args = line.args.map(([name, value]) => `${name}: ${expr(value)}`).join(', ')
    const parts = [`${line.warmup ? 'warmup ' : ''}auto(${args})`, ...modParts(line.mods)]
    out.push(pad + parts.join(' ') + '\n')
    return
  }
  const label = line.label.length === 0 ? '' : ` ${quote(line.label)}`
  out.push(`${pad}dynamic${label} {\n`)
  line.body.forEach((statement) => {
    serializeStmt(out, statement, depth + 1)
  })
  out.push(`${pad}}\n`)
}

export function setLine(line: SetLine): string {
  const parts: string[] = []
  if (line.warmup) {
    parts.push('warmup')
  }
  if (line.count) {
    parts.push(`${expr(line.count)} x`)
  }
  parts.push(expr(line.primary))
  if (line.weight) {
    parts.push(`@ ${expr(line.weight)}`)
  }
  parts.push(...modParts(line.mods))
  return parts.join(' ')
}

/** The trailing part any line can wear: the rest before it, the filming, the note. */
function modParts(mods: Mods): string[] {
  const parts: string[] = []
  if (mods.rest) {
    parts.push(`rest ${expr(mods.rest)}`)
  }
  if (mods.video === true) {
    parts.push('video')
  }
  if (mods.notes !== undefined) {
    parts.push(quote(mods.notes))
  }
  return parts
}

function serializeStmt(out: string[], stmt: Stmt, depth: number): void {
  const pad = INDENT.repeat(depth)
  switch (stmt.type) {
    case 'declare':
      out.push(`${pad}${stmt.mutable ? 'let' : 'const'} ${stmt.name} = ${expr(stmt.value)}\n`)
      break
    case 'assign':
      out.push(`${pad}${expr(stmt.target)} ${stmt.op} ${expr(stmt.value)}\n`)
      break
    case 'if': {
      out.push(`${pad}if (${expr(stmt.condition)}) {\n`)
      stmt.then.forEach((inner) => {
        serializeStmt(out, inner, depth + 1)
      })
      if (stmt.otherwise.length === 0) {
        out.push(`${pad}}\n`)
      } else {
        out.push(`${pad}} else {\n`)
        stmt.otherwise.forEach((inner) => {
          serializeStmt(out, inner, depth + 1)
        })
        out.push(`${pad}}\n`)
      }
      break
    }
    case 'for': {
      const init = stmt.init ? inline(stmt.init) : ''
      const step = stmt.step ? inline(stmt.step) : ''
      out.push(`${pad}for (${init}; ${stmt.condition ? expr(stmt.condition) : ''}; ${step}) {\n`)
      stmt.body.forEach((inner) => {
        serializeStmt(out, inner, depth + 1)
      })
      out.push(`${pad}}\n`)
      break
    }
    case 'forOf': {
      out.push(`${pad}for (const ${stmt.name} of ${expr(stmt.source)}) {\n`)
      stmt.body.forEach((inner) => {
        serializeStmt(out, inner, depth + 1)
      })
      out.push(`${pad}}\n`)
      break
    }
    case 'yield':
      out.push(`${pad}yield ${setLine(stmt.line)}\n`)
      break
    case 'return':
      out.push(`${pad}return${stmt.line ? ` ${setLine(stmt.line)}` : ''}\n`)
      break
    case 'expr':
      out.push(`${pad}${expr(stmt.expr)}\n`)
      break
  }
}

/** A statement written on one line, for the head of a counting `for`. */
function inline(stmt: Stmt): string {
  switch (stmt.type) {
    case 'declare':
      return `${stmt.mutable ? 'let' : 'const'} ${stmt.name} = ${expr(stmt.value)}`
    case 'assign':
      return `${expr(stmt.target)} ${stmt.op} ${expr(stmt.value)}`
    case 'expr':
      return expr(stmt.expr)
    default:
      return ''
  }
}

function props(list: readonly Prop[]): string {
  if (list.length === 0) {
    return ''
  }
  return ` (${list.map((prop) => `${prop.name}: ${expr(prop.value)}`).join(', ')})`
}

/*
 * Text is written on one line, so a line break inside it goes back the way the lexer reads it
 * — as `\n`. Without that, a description of more than one paragraph writes a script that will
 * not parse again.
 */
function quote(value: string): string {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`
}

/** How tightly an operator binds, so only the parentheses that are needed are written. */
function binding(op: string): number {
  switch (op) {
    case '??':
      return 1
    case '||':
      return 2
    case '&&':
      return 3
    case '===':
    case '!==':
      return 4
    case '<':
    case '>':
    case '<=':
    case '>=':
      return 5
    case '+':
    case '-':
      return 6
    default:
      return 7
  }
}

export function expr(node: Expr): string {
  switch (node.type) {
    case 'number':
      return formatNumber(node.value * node.unit.factor, node.unit)
    case 'text':
      return quote(node.value)
    case 'name':
      return node.name
    case 'hole':
      return `$${node.name}`
    case 'member':
      return `${expr(node.target)}${node.optional ? '?.' : '.'}${node.name}`
    case 'index':
      return `${expr(node.target)}[${expr(node.index)}]`
    case 'array':
      return `[${node.items.map(expr).join(', ')}]`
    case 'arrow': {
      const only = node.params.length === 1 ? node.params[0] : undefined
      const params = only ?? `(${node.params.join(', ')})`
      return `${params} => ${expr(node.body)}`
    }
    case 'unary':
      return `${node.op}${expr(node.operand)}`
    case 'ternary':
      return `${expr(node.condition)} ? ${expr(node.then)} : ${expr(node.otherwise)}`
    case 'binary': {
      const level = binding(node.op)
      const left =
        node.left.type === 'binary' && binding(node.left.op) < level
          ? `(${expr(node.left)})`
          : expr(node.left)
      const right =
        node.right.type === 'binary' && binding(node.right.op) <= level
          ? `(${expr(node.right)})`
          : expr(node.right)
      return `${left} ${node.op} ${right}`
    }
    case 'call': {
      const args = [
        ...node.args.map(expr),
        ...node.named.map(([name, value]) => `${name}: ${expr(value)}`),
      ]
      return `${expr(node.callee)}(${args.join(', ')})`
    }
  }
}

/** Formatting a script twice changes nothing, which is what makes a shape comparable. */
export function canonical(source: string): string {
  return serialize(parseTemplate(source))
}
